import { useState } from "react";
import { FileText, Sparkles, CheckCircle2, UserCheck, HelpCircle } from "lucide-react";

const ORIGINAL = "Responsible for marketing campaigns, social media, digital marketing and collaborating with different departments.";
const IMPROVED = "Led cross-functional digital marketing campaigns across three markets, coordinating paid media, content strategy and performance reporting.";

const EVIDENCE = [
  { id: "supported", icon: CheckCircle2, label: "Supported by your CV", text: "Campaigns, social media and teamwork appear in the original CV.", cls: "text-kesh-text bg-kesh-soft border-kesh/25" },
  { id: "confirmed", icon: UserCheck, label: "Confirmed by you", text: "\"Three markets\" and \"paid media\" were added only after the candidate confirmed them.", cls: "text-brand-text bg-brand-soft border-brand/25" },
  { id: "required", icon: HelpCircle, label: "Additional information required", text: "Want to mention results, like leads or growth? You'd be asked for the real numbers. We never make them up.", cls: "text-warning bg-warning-soft border-warning/30" },
];

export const CvDemo = () => {
  const [improved, setImproved] = useState(true);
  return (
    <div data-testid="interactive-cv-demo" className="gi-card p-5 sm:p-7">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 text-body">
          <FileText className="w-4 h-4" aria-hidden="true" />
          <span className="text-sm font-medium">Marketing Executive · CV bullet</span>
        </div>
        <div role="tablist" aria-label="CV version" className="flex p-1 rounded-xl bg-subtle">
          {[[false, "Original", "demo-toggle-original"], [true, "Improved", "demo-toggle-improved"]].map(([v, l, t]) => (
            <button key={l} role="tab" aria-selected={improved === v} data-testid={t} onClick={() => setImproved(v)}
              className={`px-4 py-2 min-h-[36px] rounded-lg text-sm font-semibold transition-colors ${improved === v ? "bg-surface text-ink shadow-card" : "text-body hover:text-ink"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div data-testid="cv-demo-container" aria-live="polite" className={`rounded-xl border p-5 min-h-[124px] transition-colors duration-300 ${improved ? "border-brand/25 bg-brand-soft/60" : "border-line bg-canvas"}`}>
        <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wide">
          {improved ? <><Sparkles className="w-3.5 h-3.5 text-brand" aria-hidden="true" /><span className="text-brand-text">Improved</span></> : <span className="text-faint">Original</span>}
        </div>
        <p key={String(improved)} className={`af-fade-up leading-relaxed text-base ${improved ? "text-ink" : "text-body"}`}>
          {improved ? IMPROVED : ORIGINAL}
        </p>
      </div>

      <div className={`mt-5 space-y-3 transition-opacity duration-300 ${improved ? "opacity-100" : "opacity-40"}`} aria-hidden={!improved}>
        {EVIDENCE.map((e) => (
          <div key={e.id} data-testid={`evidence-tag-${e.id}`} className="flex flex-col sm:flex-row gap-1.5 sm:gap-3 sm:items-start">
            <span className={`shrink-0 self-start inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${e.cls}`}>
              <e.icon className="w-3.5 h-3.5" aria-hidden="true" /> {e.label}
            </span>
            <span className="text-sm text-body leading-relaxed">{e.text}</span>
          </div>
        ))}
      </div>

      <p className="mt-5 text-sm text-faint" data-testid="cv-demo-disclaimer">Illustrative example, not an actual customer result.</p>
    </div>
  );
};
